/* ================================================================
   Legendary USA — Mobile Drawer (v1.2)
   Drilldown panel system with stateful navigation memory.

   v1.2 — Phase 3 polish:
     • Stateful nav memory (sessionStorage) — last panel + scroll
       positions persist across page navigations within the tab.
     • Focus trap when drawer is open (Tab cycles, Shift-Tab too).
     • aria-current="page" on links matching the current URL.
     • Removed auto-reset-to-root on close (replaced by memory).
   ================================================================ */
(function () {
  if (customElements.get('mobile-drawer')) return;

  const STORAGE_KEY = 'mdrw:state:v1';
  const STORAGE_TTL_MS = 30 * 60 * 1000; // 30min — beyond this, reset to root

  class MobileDrawer extends HTMLElement {
    connectedCallback() {
      this.drawer = this.closest('#mobile-menu') || this;
      this.viewport = this.querySelector('.mdrw__viewport');
      this.panels = Array.from(this.querySelectorAll('[data-panel]'));
      this.openButtons = Array.from(this.querySelectorAll('[data-open-panel]'));
      this.backButtons = Array.from(this.querySelectorAll('[data-back]'));

      this._panelStack = ['root'];
      this._lastOpenedAt = 0;
      this._scrollPositions = {};

      // Wire up category card → panel open
      this.openButtons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const handle = btn.getAttribute('data-open-panel');
          if (handle) this.openPanel(handle, btn);
        });
      });

      // Wire up back button → return to root
      this.backButtons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.popPanel();
        });
      });

      // Save scroll position as user scrolls inside any panel
      this.panels.forEach((p) => {
        const scroller = p.querySelector('.mdrw__panel-scroll');
        if (!scroller) return;
        scroller.addEventListener('scroll', () => {
          const handle = p.getAttribute('data-panel');
          if (handle) this._scrollPositions[handle] = scroller.scrollTop;
          this._saveState();
        }, { passive: true });
      });

      // Persist state when drawer closes (don't reset — memory restores it)
      this._observeDrawerClose();

      // Apply aria-current to links matching the current page
      this._applyAriaCurrent();

      // Focus trap setup — only active while the drawer is open
      this._installFocusTrap();

      // Restore state from sessionStorage if it's fresh
      this._restoreState();
    }

    // ─── State persistence ──────────────────────────────

    _saveState() {
      try {
        const state = {
          panel: this._panelStack[this._panelStack.length - 1] || 'root',
          stack: this._panelStack.slice(),
          scroll: this._scrollPositions,
          savedAt: Date.now(),
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch (e) {
        // sessionStorage may be unavailable (e.g. Safari private mode quirks)
      }
    }

    _restoreState() {
      let state;
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        state = JSON.parse(raw);
      } catch (e) {
        return;
      }
      if (!state || !state.savedAt) return;

      // Beyond TTL: treat as a stale session, ignore
      if (Date.now() - state.savedAt > STORAGE_TTL_MS) {
        sessionStorage.removeItem(STORAGE_KEY);
        return;
      }

      if (state.scroll && typeof state.scroll === 'object') {
        this._scrollPositions = state.scroll;
      }

      // Restore panel stack if it points to something other than root
      if (Array.isArray(state.stack) && state.stack.length > 1) {
        const lastPanel = state.stack[state.stack.length - 1];
        const target = this.querySelector(`[data-panel="${CSS.escape(lastPanel)}"]`);
        if (target) {
          this._panelStack = state.stack.slice();
          this._setActivePanel(lastPanel, { skipScrollReset: true });
          // Restore the scroll position on the restored panel
          const scroller = target.querySelector('.mdrw__panel-scroll');
          if (scroller && this._scrollPositions[lastPanel] != null) {
            scroller.scrollTop = this._scrollPositions[lastPanel];
          }
          // Mark the source card as expanded
          const sourceBtn = this.querySelector(`[data-open-panel="${CSS.escape(lastPanel)}"]`);
          if (sourceBtn) sourceBtn.setAttribute('aria-expanded', 'true');
        }
      }
    }

    _observeDrawerClose() {
      const toggle = document.querySelector('.mobile-toggle-wrapper');
      if (!toggle) return;
      const observer = new MutationObserver(() => {
        if (toggle.classList.contains('active')) {
          // Drawer opened — kick the focus trap to active state
          this._activateFocusTrap();
        } else {
          // Drawer closed — persist current state for next open
          this._saveState();
          this._deactivateFocusTrap();
        }
      });
      observer.observe(toggle, { attributes: true, attributeFilter: ['class'] });
    }

    // ─── Panel navigation ────────────────────────────────

    openPanel(handle, sourceBtn) {
      const now = performance.now();
      if (now - this._lastOpenedAt < 240) return;
      this._lastOpenedAt = now;

      const target = this.querySelector(`[data-panel="${CSS.escape(handle)}"]`);
      if (!target) return;

      this._panelStack.push(handle);
      this._setActivePanel(handle);
      this._saveState();

      window.setTimeout(() => {
        const back = target.querySelector('[data-back]');
        if (back) back.focus({ preventScroll: true });
      }, 300);

      if (sourceBtn) sourceBtn.setAttribute('aria-expanded', 'true');
    }

    popPanel() {
      if (this._panelStack.length <= 1) return;
      const closing = this._panelStack.pop();
      const newTop = this._panelStack[this._panelStack.length - 1] || 'root';
      this._setActivePanel(newTop, { restoreScroll: true });
      this._saveState();

      const sourceBtn = this.querySelector(`[data-open-panel="${CSS.escape(closing)}"]`);
      if (sourceBtn) {
        sourceBtn.setAttribute('aria-expanded', 'false');
        window.setTimeout(() => sourceBtn.focus({ preventScroll: true }), 300);
      }
    }

    _setActivePanel(handle, opts) {
      opts = opts || {};
      this.panels.forEach((p) => {
        const isActive = p.getAttribute('data-panel') === handle;
        p.classList.toggle('is-active', isActive);
        p.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        if (isActive) {
          const scroller = p.querySelector('.mdrw__panel-scroll');
          if (scroller) {
            if (opts.restoreScroll && this._scrollPositions[handle] != null) {
              scroller.scrollTop = this._scrollPositions[handle];
            } else if (!opts.skipScrollReset) {
              scroller.scrollTop = 0;
            }
          }
        }
      });
      this.drawer.setAttribute('data-panel-active', handle);
    }

    // ─── aria-current on current-page links ─────────────────────

    _applyAriaCurrent() {
      const here = window.location.pathname.replace(/\/$/, '');
      if (!here) return;
      const links = this.querySelectorAll('a[href]');
      links.forEach((a) => {
        let href = a.getAttribute('href') || '';
        // Normalize: strip query, hash, trailing slash
        href = href.split('?')[0].split('#')[0].replace(/\/$/, '');
        if (!href) return;
        if (href === here) {
          a.setAttribute('aria-current', 'page');
          a.classList.add('mdrw__link--current');
        }
      });
    }

    // ─── Focus trap ─────────────────────────────────────

    _installFocusTrap() {
      this._trapHandler = (e) => {
        if (e.key !== 'Tab') return;
        if (!this._isDrawerOpen()) return;

        const focusables = this._getFocusables();
        if (!focusables.length) return;

        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;

        if (e.shiftKey) {
          if (active === first || !this.contains(active)) {
            e.preventDefault();
            last.focus({ preventScroll: true });
          }
        } else {
          if (active === last) {
            e.preventDefault();
            first.focus({ preventScroll: true });
          }
        }
      };
    }

    _activateFocusTrap() {
      if (!this._trapHandler || this._trapActive) return;
      document.addEventListener('keydown', this._trapHandler);
      this._trapActive = true;
    }

    _deactivateFocusTrap() {
      if (!this._trapHandler || !this._trapActive) return;
      document.removeEventListener('keydown', this._trapHandler);
      this._trapActive = false;
    }

    _isDrawerOpen() {
      const toggle = document.querySelector('.mobile-toggle-wrapper');
      return !!(toggle && toggle.classList.contains('active'));
    }

    _getFocusables() {
      // Only focus elements inside the currently active panel — prevents
      // Tab from cycling through hidden panels (which have visibility:hidden
      // but the elements are still technically focusable without aria).
      const activePanel = this.querySelector('.mdrw__panel.is-active');
      if (!activePanel) return [];
      const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      return Array.from(activePanel.querySelectorAll(selector)).filter((el) => {
        // Filter out elements with display:none or visibility:hidden
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && style.visibility !== 'hidden' && el.offsetParent !== null;
      });
    }
  }

  customElements.define('mobile-drawer', MobileDrawer);
})();
